import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../../utils/apiCaller';
import { toast } from 'react-toastify';
import MentionInput from '../../../components/MentionInput/MentionInput';
import MentionText from '../../../components/MentionInput/MentionText';

interface Comment {
    id: number;
    comment: string;
    user: string;
    userId: number;
    createdAt: string;
}

interface Props {
    productId: number;
    responsibilityId: number | null;
    materialId?: number | null;
    type?: 'comment' | 'admin_warning';
    label: string;
    placeholder?: string;
    variant?: 'default' | 'admin';
    onLoaded?: () => void;
    isAdmin?: boolean;
    onCommentPosted?: () => void;
    readOnly?: boolean;
}

const AVATAR_COLORS = ['#64748b', '#6b7280', '#71717a', '#78716c', '#737373'];

const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
    return name.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase();
};

const CommentThread: React.FC<Props> = ({ productId, responsibilityId, materialId, type, label, placeholder, onLoaded, onCommentPosted, readOnly }) => {
    const intl = useIntl();
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [posting, setPosting] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const fetchComments = () => {
        let qs = `productId=${productId}&type=${type || 'comment'}`;
        if (responsibilityId != null) qs += `&responsibilityId=${responsibilityId}`;
        if (materialId) qs += `&materialId=${materialId}`;
        axiosCaller('GET', `production-comments?${qs}`).then(res => {
            setComments(res?.data || []);
            onLoaded?.();
        }).catch(() => { onLoaded?.(); });
    };

    useEffect(() => { fetchComments(); }, [productId, responsibilityId, materialId, type]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [comments]);

    const handlePost = async () => {
        if (!newComment.trim()) return;
        setPosting(true);
        try {
            const res = await axiosCaller('POST', 'production-comments', {
                productId,
                responsibilityId: responsibilityId || null,
                materialId: materialId || null,
                comment: newComment.trim(),
                type: type || 'comment',
            });
            setComments(prev => [...prev, res?.data]);
            setNewComment('');
            onCommentPosted?.();
        } catch {
            toast.error(intl.formatMessage({ id: 'production.toast.somethingWentWrong', defaultMessage: 'Diçka nuk shkoi mirë!' }));
        } finally {
            setPosting(false);
        }
    };

    return (
        <div className="ct-container">
            <label className="ct-label">{label}</label>
            <div className="ct-thread" ref={listRef}>
                {comments.length === 0 ? (
                    <div className="ct-empty"><FormattedMessage id="production.comments.empty" defaultMessage="Nuk ka komente" /></div>
                ) : (
                    comments.map(c => {
                        const color = getAvatarColor(c.user);
                        return (
                            <div key={c.id} className="ct-comment">
                                <div className="ct-avatar" style={{ background: color }}>{getInitials(c.user)}</div>
                                <div className="ct-content">
                                    <div className="ct-meta">
                                        <span className="ct-user">{c.user}</span>
                                        <span className="ct-date">{c.createdAt}</span>
                                    </div>
                                    {/* MentionText highlights @username tokens as chips.
                                        Falls back to plain text rendering when no mentions
                                        are present, so existing comments without mentions
                                        look identical to before. */}
                                    <div className="ct-text"><MentionText text={c.comment} /></div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            {!readOnly && (
                <>
                    <div className="ct-input-row">
                        {/* MentionInput layers @ autocomplete on top of a controlled
                            <textarea> (multiline). Enter submits; Shift+Enter falls
                            through to the textarea and inserts a newline, so workers
                            can write multi-line explanations. When the mention popover
                            is open, MentionInput swallows Enter (selecting the mention
                            instead) before our handler runs. */}
                        <MentionInput
                            multiline
                            rows={2}
                            className="ct-input"
                            value={newComment}
                            onChange={(next) => setNewComment(next)}
                            placeholder={placeholder}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
                        />
                        <button className="ct-send" onClick={handlePost} disabled={posting || !newComment.trim()} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                        </button>
                    </div>
                    <div className="ct-hint">
                        <FormattedMessage id="production.comments.enterHint" defaultMessage="Enter për të dërguar · Shift+Enter për rresht të ri" />
                    </div>
                </>
            )}
        </div>
    );
};

export default CommentThread;
